import { useQueries } from '@tanstack/react-query'
import { getProjectMembers, type ProjectMember, type ProjectRow } from '../transport/api'
import type { BoardInfo } from '../chatv1/transport'
import { MAIN_BOARD } from './boardNav'

/**
 * THE ROOMS AN AGENT IS IN — for its session's whiteboard drawer (owner, 2026-11-01: 在一個 Session 裡面新增一個抽屜,
 * 使用者拉出來的時候可以看到這個 Agent 他所在的白板).
 *
 * Nothing on the service lists an agent's rooms, so each of this person's rooms is asked who is in it — the query the
 * room's roster reads (['proj-members', conv]), which the `projects_changed` frame already refreshes when someone comes
 * or goes. Read once per room and kept: a room's roster changes by that frame, not by the window regaining focus.
 * A friend's chat holds no agents and is not asked.
 */
export function useAgentRooms(projects: ProjectRow[], hash: string | null): ProjectRow[] {
  const rooms = hash ? projects.filter((p) => !p.dm_peer) : []
  const qs = useQueries({
    queries: rooms.map((r) => ({
      queryKey: ['proj-members', r.project_id],
      queryFn: () => getProjectMembers(r.project_id),
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    })),
  })
  return rooms.filter((_, i) => seats(qs[i]?.data?.members, hash!))
}

/** The agent is one of the room's members. */
function seats(members: ProjectMember[] | undefined, hash: string): boolean {
  return !!members?.some((m) => m.kind === 'agent' && m.channel_hash === hash)
}

/**
 * Which of its rooms' boards the drawer opens on: the one the agent wrote to last (a board remembers its last writer
 * when that was an agent — chat lambda/boards.ts `last_via`); else, in its first room, the board this device had open
 * there last (`last`), when the room still has it; else that room's first board.
 */
export function agentBoard(
  rooms: string[],
  boards: (BoardInfo[] | undefined)[],
  hash: string,
  last: (conv: string) => string,
): { conv: string; board: string } | null {
  let best: { conv: string; board: string } | null = null
  let at = -1
  rooms.forEach((conv, i) => {
    for (const b of boards[i] ?? []) {
      if (b.last_via === hash && (b.last_ts ?? 0) > at) {
        best = { conv, board: b.board }
        at = b.last_ts ?? 0
      }
    }
  })
  if (best) return best
  const conv = rooms[0]
  if (!conv) return null
  const mine = last(conv)
  const known = boards[0]
  return { conv, board: !known || known.some((b) => b.board === mine) ? mine : MAIN_BOARD }
}
